import { debounce } from 'lodash';
import React, { useCallback, useDebugValue, useEffect, useMemo } from 'react';
import { shallowEqual } from 'react-redux';
import { Channel, Event, StreamChat } from 'stream-chat';

import { getSafeElectronAPI } from 'src/hooks/useElectron';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import { useUnreadMessageViewer } from 'src/hooks/useUnreadMessageViewer';
import { useChatClient } from 'src/legacy/components/Chat/useChatClient';
import { useGetNotificationsSettingsQuery } from 'src/services/api';
import {
  incrementTotalUnreadMessagesByCompany,
  setChannelIdToCompanyId,
  setTotalUnreadMessages,
  setTotalUnreadMessagesByCompany,
} from 'src/store/messages/actions';
import {
  getChannelsWithUser,
  getDerivedTotalUnreadMessageCount,
  isStreamRateLimitError,
} from 'src/utils/MessagingUtils';

import { PortalConfigContext } from '../context';
import { useGetInboxNotificationsQuery } from '../services/api/inboxApi';
import { formatNotificationCount } from '../utils/NotificationsUtils';

import { useNotificationsCounts } from './useNotificationsCount';

const channelWithCompanyID = (
  channel: Channel,
): channel is Channel & {
  data: { companyID: string };
} => {
  return Boolean(
    channel.data &&
      'companyID' in channel.data &&
      channel.data?.companyID &&
      typeof channel.data.companyID === 'string',
  );
};

/**
 * A custom React hook that calculates the notification count for internal or client users
 * and updates the page title with the count and title.
 *
 * This hook uses data from the Redux store to determine notification counts and
 * uses the `useEffect` hook to update the document title based on the notification counts.
 *
 * @returns {void}
 */
export const useUserNotification = (isClientUser: boolean) => {
  // Fetch inbox notifications using the useGetInboxNotificationsQuery hook
  const { data: inboxNotifications = [] } = useGetInboxNotificationsQuery();
  const portalConfig = React.useContext(PortalConfigContext);

  const { data: notificationSettings } = useGetNotificationsSettingsQuery(
    undefined,
    {
      skip: isClientUser,
    },
  );
  const dispatch = useAppDispatch();
  const chatClient = useChatClient();
  const currentUserChatId = useAppSelector(
    (state) => state.user.data?.getstreamId,
  );
  const channelIdToCompanyId = useAppSelector(
    (state) => state.messages.channelIdToCompanyId,
  );
  const unreadViewer = useUnreadMessageViewer();

  const disabledProductNotification = notificationSettings?.disableInProduct;
  const disabledMessagesNotifications =
    notificationSettings?.notifyAbout.newMessages.disableInProduct;
  // Extract relevant state properties using useSelector from the Redux store
  const {
    activeCompanyId,
    isClient,
    totalUnreadMessages,
    clients,
    internalUsers,
    companyMap,
  } = useAppSelector(
    (state) => ({
      totalUnreadMessages: state.messages.totalUnreadMessages || 0,
      isClient: state.user.isClient,
      clients: state.clients.clients,
      internalUsers: state.users.internalUsers,
      companyMap: state.clients.companyDataMap,
      activeCompanyId: state.activeCompany.activeCompanyId,
    }),
    shallowEqual,
  );

  const activeCompanyNotifications = inboxNotifications.filter(
    (notification) => {
      if (!notification.companyId) return true;
      return isClient ? notification.companyId === activeCompanyId : true;
    },
  );

  // Calculate the count of unread inbox notifications
  const notificationCounts = useNotificationsCounts(
    activeCompanyNotifications,
    [...clients, ...internalUsers],
    companyMap,
  );

  /**
   * Calculates the notification count for internal users by summing up
   * the total unread messages, unread inbox notifications, and form notifications.
   *
   * @returns {number} The total notification count for internal users.
   */
  const getInternalUserNotificationCount = (): number => {
    const disableMessagesNotifications =
      notificationSettings?.notifyAbout.newMessages.disableInProduct;

    // only count unread messages when the in-product notifications are allowed
    const unreadMessages = disableMessagesNotifications
      ? 0
      : totalUnreadMessages;

    return unreadMessages + notificationCounts.unreadNotificationCount;
  };

  /**
   * Calculates the notification count for client users by summing up
   * the total unread messages, unread inbox notifications, billing notifications, file notifications and form notifications.
   *
   * @returns {number} The total notification count for client users.
   */
  const getClientUserNotificationCount = (): number => {
    return totalUnreadMessages + notificationCounts.unreadNotificationCount;
  };

  // Calculate the total notification count for client and internal users
  const totalClientNotifications = getClientUserNotificationCount();
  const totalInternalNotifications = getInternalUserNotificationCount();

  useDebugValue({
    totalClientNotifications,
    totalInternalNotifications,
    totalUnreadMessages,
  });

  /**
   * We need to fetch the unread count for each company when the app mounts.
   * This function fetches all current user's channels and sets the unread count for each company.
   */
  const fetchUnreadMessagesCountByCompany = useCallback(
    async (client: StreamChat) => {
      if (!currentUserChatId) return;
      const userChannels = await getChannelsWithUser(
        { memberId: currentUserChatId, companyId: null },
        client,
      );

      userChannels.forEach((channel) => {
        if (channelWithCompanyID(channel)) {
          dispatch(
            setChannelIdToCompanyId(channel.cid, channel.data.companyID),
          );
          dispatch(
            setTotalUnreadMessagesByCompany(
              channel.data.companyID,
              channel.countUnread(),
            ),
          );
        }
      });
    },
    [currentUserChatId, dispatch],
  );

  const debouncedUpdateMessagingUnreadCount = useMemo(
    () =>
      debounce(
        async (userId: string | undefined) => {
          if (!chatClient || !userId) return;

          try {
            let messagingUnreadCount = 0;
            if (unreadViewer.isInternalUser) {
              messagingUnreadCount = await getDerivedTotalUnreadMessageCount(
                chatClient,
                unreadViewer,
              );
            } else {
              const response = await chatClient.getUnreadCount(userId);
              response?.channel_type?.forEach((channelType) => {
                if (channelType.channel_type === 'messaging') {
                  messagingUnreadCount = channelType.unread_count;
                }
              });
            }
            dispatch(setTotalUnreadMessages(messagingUnreadCount));
          } catch (error: any) {
            if (isStreamRateLimitError(error)) {
              console.warn('Rate limited on getUnreadCount, skipping update');
              return;
            }
            throw error;
          }
        },
        1000,
        { leading: true, trailing: true },
      ),
    [chatClient, dispatch, unreadViewer],
  );

  const clientEventHandler = useCallback(
    (event: Event) => {
      if (event.unread_count !== undefined) {
        // We can no longer rely on the unread_count from the event object. Since
        // it will include unread count for messaging/internal channels. We need to
        // use the unread count api to separate the unread count by channel type.
        // For IU viewers, always re-derive from in-memory channels — never trust
        // `event.unread_count` directly. That value is GetStream's native total
        // (across messaging + internal channels, IU-authored included), so using
        // it as a shortcut in the 0 case or any case leaks wrong totals for IU.
        if (!isClient) {
          void debouncedUpdateMessagingUnreadCount(currentUserChatId);
        } else {
          dispatch(setTotalUnreadMessages(event.unread_count));
        }

        if (!isClient) return;
        // the event object does not have the channel data with the company id
        // it only has the channel id known as cid getstream field.
        // However a cid does not work to store the unread count. We need the company id.
        // For that we do have a mapping of cid to company id which will help us to
        // lookup the company id from the cid.
        const companyId = event?.cid
          ? channelIdToCompanyId?.[event?.cid]
          : undefined;

        // once we get the company id from the cid, we do update the unread count state.
        if (
          event.type === 'message.new' &&
          chatClient?.user?.id !== event.user?.id
        ) {
          if (companyId) {
            dispatch(incrementTotalUnreadMessagesByCompany(companyId, 1));
          }
        } else if (chatClient && event.type === 'notification.mark_read') {
          void fetchUnreadMessagesCountByCompany(chatClient);
        }
      }
    },
    [
      channelIdToCompanyId,
      chatClient,
      currentUserChatId,
      debouncedUpdateMessagingUnreadCount,
      dispatch,
      fetchUnreadMessagesCountByCompany,
      isClient,
    ],
  );

  /**
   * Seed per-viewer unread state on chat client ready.
   *   - Client viewers: per-company unread map.
   *   - IU viewers: derived total from unread-channel pagination so global
   *     counts do not depend on the channels currently loaded in the sidebar.
   */
  React.useEffect(() => {
    if (!chatClient || !currentUserChatId) return;

    if (isClient) {
      void fetchUnreadMessagesCountByCompany(chatClient);
      return;
    }

    let cancelled = false;
    void getDerivedTotalUnreadMessageCount(chatClient, unreadViewer)
      .then((totalUnreadMessageCount) => {
        if (cancelled) return;
        dispatch(setTotalUnreadMessages(totalUnreadMessageCount));
      })
      .catch((error) => {
        console.error('Failed to compute IU derived unread count', error);
      });
    return () => {
      cancelled = true;
    };
  }, [
    chatClient,
    isClient,
    currentUserChatId,
    unreadViewer,
    dispatch,
    fetchUnreadMessagesCountByCompany,
  ]);

  /**
   * This effect is responsible of loading unread chat notifications and subscribing to new events.
   */
  useEffect(() => {
    if (
      !chatClient ||
      !chatClient.user ||
      !currentUserChatId ||
      disabledMessagesNotifications ||
      disabledProductNotification
    ) {
      return;
    }

    // Listen for new events. This will also update the unread count for each company.
    const handleClientEvent = (event: Event) => {
      clientEventHandler(event);
    };

    chatClient.on(handleClientEvent);

    return () => {
      chatClient.off(handleClientEvent);
    };
  }, [
    chatClient,
    currentUserChatId,
    isClient,
    disabledMessagesNotifications,
    disabledProductNotification,
    clientEventHandler,
  ]);

  useEffect(() => {
    return () => {
      debouncedUpdateMessagingUnreadCount.cancel();
    };
  }, [debouncedUpdateMessagingUnreadCount]);

  // Update the document title based on the user type and notification counts
  useEffect(() => {
    const notificationCount = isClient
      ? totalClientNotifications
      : totalInternalNotifications;
    const pageTitle = isClient
      ? portalConfig.metadata?.title || portalConfig.name
      : portalConfig.metadata.titleOverride;

    document.title = `${
      notificationCount > 0
        ? `(${formatNotificationCount(notificationCount)})`
        : ''
    } ${pageTitle}`;
    getSafeElectronAPI().setBadgeCount(notificationCount);
  }, [
    totalClientNotifications,
    totalInternalNotifications,
    portalConfig,
    isClient,
  ]);
};
